import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Trash2, FileText, RefreshCw, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, formatApiErrorDetail } from "../lib/api";

const DocForm = ({ doc, onDone }) => {
  const editing = !!doc;
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(doc?.title || "");
  const [tag, setTag] = useState(doc?.tag || "");
  const [text, setText] = useState(doc?.text || "");
  const [meta, setMeta] = useState(doc?.meta || "");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!editing && !file) { toast.error("Selecciona un PDF"); return; }
    const fd = new FormData();
    fd.append("title", title);
    fd.append("tag", tag);
    fd.append("text", text);
    fd.append("meta", meta);
    if (file) fd.append("file", file);
    setLoading(true);
    try {
      if (editing) await api.put(`/admin/library/${doc.id}`, fd);
      else await api.post("/admin/library", fd);
      toast.success(editing ? "Documento actualizado" : "Documento publicado");
      onDone();
      setOpen(false);
      if (!editing) { setTitle(""); setTag(""); setText(""); setMeta(""); setFile(null); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing ? (
          <button data-testid={`lib-edit-${doc.id}`} className="inline-flex items-center gap-1.5 text-xs text-rc-sage hover:text-rc-forestlight transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Editar / Reemplazar
          </button>
        ) : (
          <button data-testid="lib-new-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-rc-forest hover:bg-rc-forestlight text-stone-100 text-sm transition-colors">
            <Plus className="w-4 h-4" /> Subir documento
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-rc-card border-rc-border text-stone-100 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-light">
            {editing ? "Editar documento" : "Nuevo documento"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="lib-form">
          <div>
            <Label className="text-stone-300">Título *</Label>
            <Input required data-testid="lib-title" value={title} onChange={(e) => setTitle(e.target.value)}
              className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-stone-300">Etiqueta</Label>
              <Input data-testid="lib-tag" value={tag} onChange={(e) => setTag(e.target.value)}
                className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
            </div>
            <div>
              <Label className="text-stone-300">Meta</Label>
              <Input data-testid="lib-meta" value={meta} onChange={(e) => setMeta(e.target.value)}
                className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
            </div>
          </div>
          <div>
            <Label className="text-stone-300">Descripción</Label>
            <Textarea rows={3} data-testid="lib-text" value={text} onChange={(e) => setText(e.target.value)}
              className="mt-1.5 bg-rc-bg2 border-rc-border text-stone-100" />
          </div>
          <div>
            <Label className="text-stone-300">{editing ? "Reemplazar PDF (opcional)" : "Archivo PDF *"}</Label>
            <input type="file" accept="application/pdf" data-testid="lib-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1.5 block w-full text-sm text-stone-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-rc-forest file:text-stone-100 hover:file:bg-rc-forestlight" />
            {editing && doc.filename && <div className="mt-1 text-xs text-stone-500">Actual: {doc.filename || "externo"}</div>}
          </div>
          <button type="submit" disabled={loading} data-testid="lib-submit"
            className="w-full py-3 rounded-full bg-rc-forest hover:bg-rc-forestlight text-stone-100 transition-colors disabled:opacity-60">
            {loading ? "Guardando…" : editing ? "Guardar cambios" : "Publicar documento"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const LibraryManager = () => {
  const [docs, setDocs] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/library").then((r) => setDocs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este documento de la biblioteca pública?")) return;
    try {
      await api.delete(`/admin/library/${id}`);
      toast.success("Documento eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-6" data-testid="library-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-500 max-w-xl font-light">
          Sube, reemplaza o elimina los documentos que aparecen en la biblioteca pública del sitio.
        </p>
        <DocForm onDone={load} />
      </div>
      <div className="space-y-3">
        {docs.map((d) => (
          <div key={d.id} className="flex items-start gap-4 p-5 rounded-xl bg-rc-card border border-rc-border" data-testid={`lib-row-${d.id}`}>
            <FileText className="w-5 h-5 text-rc-sage mt-1 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-mono text-rc-sage uppercase tracking-wider">{d.tag}</div>
              <div className="font-serif text-lg text-stone-100">{d.title}</div>
              <div className="text-xs text-stone-600 mt-1">
                {d.meta} {d.is_external ? "· enlace externo" : `· ${d.filename || "archivo subido"}`}
              </div>
              <div className="mt-3 flex gap-5">
                <DocForm doc={d} onDone={load} />
                <button onClick={() => remove(d.id)} data-testid={`lib-delete-${d.id}`}
                  className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-red-400 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
